import { useMutation } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Divider, Grid, IconButton, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { GetShippingDocumentQuery, UnitType, WasteKind } from "generated-graphql/graphql";
import { Control, useFieldArray, useForm, UseFormGetValues } from "react-hook-form";
import { UNIT_LABELS } from "routes/Customer/Waste/Containers/containerDisplay";
import { SHIPPING_PAPERWORK_QUERY } from "routes/Customer/Waste/Manifests/api";

import { GET_SHIPPING_DOCUMENT_QUERY, SAVE_WASTE_SHIPPING_DOCUMENT } from "./api";
import { DECLARED_WASTE_KIND_LABELS } from "./extractionReview";
import { SHIPPING_DOCUMENT_KIND_OPTIONS } from "./shippingDocumentDisplay";
import {
  EMPTY_LINE,
  ShippingDocumentFormValues,
  toFormValues,
  toSaveInput,
} from "./shippingDocumentForm";

const HALF = { xs: 12, sm: 6 };

const UNIT_OPTIONS = Object.values(UnitType).map((value) => ({ value, label: UNIT_LABELS[value] }));
const DECLARED_WASTE_KIND_OPTIONS = [
  { value: null, display: "—" },
  ...Object.values(WasteKind).map((value) => ({
    value,
    display: DECLARED_WASTE_KIND_LABELS[value],
  })),
];

type Document = NonNullable<GetShippingDocumentQuery["wasteShippingDocument"]>;
type FormControl = Control<ShippingDocumentFormValues>;

/** Kind/documentNumber/generator/transporter/destination/shipDate — the header half of the form. */
function HeaderFields({ control }: { control: FormControl }) {
  return (
    <Grid container spacing={2}>
      <Grid size={HALF}>
        <FormSelect
          name="kind"
          label="Kind"
          control={control}
          hideErrors
          selectItems={SHIPPING_DOCUMENT_KIND_OPTIONS.map((option) => ({
            value: option.value,
            display: option.label,
          }))}
        />
      </Grid>
      <Grid size={HALF}>
        <FormTextField name="documentNumber" label="Document number" control={control} fullWidth />
      </Grid>
      <Grid size={HALF}>
        <FormTextField name="generatorName" label="Generator" control={control} fullWidth />
      </Grid>
      <Grid size={HALF}>
        <FormTextField name="transporterName" label="Transporter" control={control} fullWidth />
      </Grid>
      <Grid size={HALF}>
        <FormTextField name="destinationName" label="Destination" control={control} fullWidth />
      </Grid>
      <Grid size={HALF}>
        <DateField name="shipDate" label="Ship date" control={control} />
      </Grid>
    </Grid>
  );
}

/** One editable line row: description, quantity, unit, declared kind, codes, remove. */
function LineRow({
  control,
  getValues,
  index,
  onRemove,
}: {
  control: FormControl;
  getValues: UseFormGetValues<ShippingDocumentFormValues>;
  index: number;
  onRemove: () => void;
}) {
  return (
    /*
     * Top-aligned, not centred. A select is a different height from a text
     * field, so centring each cell against the tallest one in the row staggers
     * them into a staircase. Hanging them all from a common top edge keeps the
     * inputs level whatever they contain.
     *
     * The remove button sits outside the field grid so the six fields get the
     * full twelve columns; squeezing it in left the codes fields too narrow for
     * their own labels, which then wrapped inside the empty input.
     */
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Grid container spacing={1} sx={{ flex: 1 }}>
        <Grid size={{ xs: 12, sm: 2 }}>
          <FormTextField
            name={`lines.${index}.description`}
            label="Description"
            control={control}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 1.5 }}>
          <FormTextField
            name={`lines.${index}.quantity`}
            label="Quantity"
            control={control}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 2.5 }}>
          <FormSelect
            name={`lines.${index}.unit`}
            label="Unit"
            control={control}
            hideErrors
            selectItems={UNIT_OPTIONS.map((option) => ({
              value: option.value,
              display: option.label,
            }))}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 2 }}>
          <FormSelect
            name={`lines.${index}.declaredWasteKind`}
            label="Declared kind"
            control={control}
            hideErrors
            selectItems={DECLARED_WASTE_KIND_OPTIONS}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 2 }}>
          <FormTextField
            name={`lines.${index}.federalWasteCodes`}
            label="Federal codes"
            control={control}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 2 }}>
          <FormTextField
            name={`lines.${index}.stateWasteCodes`}
            label="State codes"
            control={control}
            fullWidth
          />
        </Grid>
      </Grid>
      <IconButton
        aria-label={`Remove line ${getValues(`lines.${index}.description`) || index + 1}`}
        onClick={onRemove}
        // Against the inputs rather than their labels, which sit above them.
        sx={{ mt: 1 }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

function LinesSection({
  control,
  getValues,
  fields,
  onAppend,
  onRemove,
}: {
  control: FormControl;
  getValues: UseFormGetValues<ShippingDocumentFormValues>;
  fields: { id: string }[];
  onAppend: () => void;
  onRemove: (index: number) => void;
}) {
  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack>
          <Typography variant="subtitle2">Lines</Typography>
          {fields.length > 0 && (
            // Once, rather than under both code fields on every row, where it
            // repeated per line and made those two fields taller than the rest.
            <Typography variant="caption" color="text.secondary">
              Separate waste codes with commas.
            </Typography>
          )}
        </Stack>
        <Button size="small" startIcon={<AddIcon />} onClick={onAppend}>
          Add line
        </Button>
      </Stack>
      {fields.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No lines yet.
        </Typography>
      )}
      {fields.map((field, index) => (
        <LineRow
          key={field.id}
          control={control}
          getValues={getValues}
          index={index}
          onRemove={() => onRemove(index)}
        />
      ))}
    </Stack>
  );
}

export type EditShippingDocumentDialogProps = {
  open: boolean;
  onClose: () => void;
  document: Document;
  onSaved: () => void;
};

/**
 * Plain react-hook-form editor for a saved `WasteShippingDocument` — the
 * grid-based `ExtractionReviewGrid` is for AI-extraction review, not this.
 * Every field is optional, matching the mutation's own input shape; the
 * lines list is always sent in full on save (wholesale-replace semantics).
 */
export function EditShippingDocumentDialog({
  open,
  onClose,
  document,
  onSaved,
}: EditShippingDocumentDialogProps) {
  const alerts = useAlerts();
  const { control, handleSubmit, getValues } = useForm<ShippingDocumentFormValues>({
    defaultValues: toFormValues(document),
  });
  const { fields, append, remove } = useFieldArray({ control, name: "lines" });

  const [save, { loading: saving }] = useMutation(SAVE_WASTE_SHIPPING_DOCUMENT, {
    // The grid behind this dialog is the merged shipping-paperwork list, and
    // `documentNumber` / `shipDate` are its own item-level fields — normalizing
    // the saved WasteShippingDocument into the cache does not touch them, so
    // without this refetch an edited row keeps its old number and date until a
    // reload. GetShippingDocument is active under the dialog and carries the
    // rest of the document's fields.
    refetchQueries: [SHIPPING_PAPERWORK_QUERY, GET_SHIPPING_DOCUMENT_QUERY],
  });

  const onSubmit = async (values: ShippingDocumentFormValues) => {
    try {
      await save({
        variables: {
          input: toSaveInput(values, { id: document.id, facilityId: document.facility?.id ?? "" }),
        },
      });
      alerts.success("Shipping document saved");
      onSaved();
    } catch (error) {
      alerts.error(
        "Could not save the shipping document",
        error instanceof Error ? error.message : undefined
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      title="Edit shipping document"
      primaryActionButtonProps={{ loading: saving, onClick: handleSubmit(onSubmit) }}
    >
      <Stack spacing={3} sx={{ pt: 1 }}>
        <HeaderFields control={control} />
        <Divider />
        <LinesSection
          control={control}
          getValues={getValues}
          fields={fields}
          onAppend={() => append(EMPTY_LINE)}
          onRemove={remove}
        />
      </Stack>
    </Dialog>
  );
}
